import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import type { OverviewMapRepository } from '../../application/OverviewMapRepository'
import type { OverviewMapPoint, OverviewMapResult } from '../../domain/overviewMapPoint'

interface SupabaseResponse {
  data: unknown
  error: { message?: string; code?: string } | null
}

function asResponse(value: unknown): SupabaseResponse {
  return value as SupabaseResponse
}

const rawPointSchema = z.object({
  id: z.string(),
  category: z.enum([
    'GREEN',
    'YELLOW',
    'OPENED',
    'BLUE',
    'ORANGE',
    'DRAFT',
    'SUSPENDED',
    'EXPIRED',
    'CANCELLED',
    'NOT_INTERESTED',
  ]),
  latitude: z.coerce.number(),
  longitude: z.coerce.number(),
  clientId: z.string().nullable().optional(),
  clientName: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  contractId: z.string().nullable().optional(),
  contractNumber: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  totalCents: z.coerce.number().nullable().optional(),
  standardDrivewayCapacity: z.coerce.number().nullable().optional(),
  season: z.string().nullable().optional(),
  clientType: z.string().nullable().optional(),
  clientSince: z.coerce.number().nullable().optional(),
  servicesSummary: z.string().nullable().optional(),
  sansFacturation: z.boolean().nullable().optional(),
  statusV2: z.string().nullable().optional(),
  sentAt: z.string().nullable().optional(),
  nextFollowUpAt: z.string().nullable().optional(),
  signedAt: z.string().nullable().optional(),
  leadId: z.string().nullable().optional(),
  leadName: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  origin: z.string().nullable().optional(),
  nextReminderAt: z.string().nullable().optional(),
})

const resultSchema = z.object({
  season: z.string().nullable(),
  categoriesVisible: z.object({
    green: z.boolean(),
    yellow: z.boolean(),
    blue: z.boolean(),
    orange: z.boolean(),
    opened: z.boolean(),
    draft: z.boolean(),
    suspended: z.boolean(),
    expired: z.boolean(),
    cancelled: z.boolean(),
    notInterested: z.boolean(),
  }),
  points: z.array(rawPointSchema),
  noCoordinatesCount: z.object({
    green: z.number(),
    yellow: z.number(),
    blue: z.number(),
    orange: z.number(),
    opened: z.number(),
    draft: z.number(),
    suspended: z.number(),
    expired: z.number(),
    cancelled: z.number(),
    notInterested: z.number(),
  }),
  noCoordinatesRecords: z.array(
    z.object({
      id: z.string(),
      category: z.enum([
        'GREEN',
        'YELLOW',
        'OPENED',
        'BLUE',
        'ORANGE',
        'DRAFT',
        'SUSPENDED',
        'EXPIRED',
        'CANCELLED',
        'NOT_INTERESTED',
      ]),
      label: z.string().nullable(),
      address: z.string().nullable().optional(),
      contractId: z.string().nullable().optional(),
      leadId: z.string().nullable().optional(),
    }),
  ),
  computedAt: z.string(),
})

function mapPoint(raw: z.infer<typeof rawPointSchema>): OverviewMapPoint | null {
  if (raw.category === 'BLUE') {
    if (!raw.leadId || !raw.status) return null
    return {
      id: raw.id,
      category: 'BLUE',
      leadId: raw.leadId,
      ...(raw.leadName ? { leadName: raw.leadName } : {}),
      ...(raw.phone ? { phone: raw.phone } : {}),
      ...(raw.address ? { address: raw.address } : {}),
      latitude: raw.latitude,
      longitude: raw.longitude,
      status: raw.status,
      ...(raw.origin ? { origin: raw.origin } : {}),
      ...(raw.nextReminderAt ? { nextReminderAt: raw.nextReminderAt } : {}),
    }
  }
  // Toutes les autres catégories sont des Contrats : même socle.
  if (!raw.clientId || !raw.clientName || !raw.contractId || !raw.contractNumber) return null
  const base = {
    id: raw.id,
    clientId: raw.clientId,
    clientName: raw.clientName,
    ...(raw.phone ? { phone: raw.phone } : {}),
    contractId: raw.contractId,
    contractNumber: raw.contractNumber,
    ...(raw.address ? { address: raw.address } : {}),
    latitude: raw.latitude,
    longitude: raw.longitude,
    totalCents: raw.totalCents ?? 0,
    ...(raw.sansFacturation ? { sansFacturation: raw.sansFacturation } : {}),
  }
  if (raw.category === 'GREEN') {
    return {
      ...base,
      category: 'GREEN',
      ...(raw.standardDrivewayCapacity ? { standardDrivewayCapacity: raw.standardDrivewayCapacity } : {}),
      ...(raw.season ? { season: raw.season } : {}),
      ...(raw.clientType ? { clientType: raw.clientType } : {}),
      ...(raw.clientSince ? { clientSince: raw.clientSince } : {}),
      ...(raw.servicesSummary ? { servicesSummary: raw.servicesSummary } : {}),
    }
  }
  if (raw.category === 'YELLOW' || raw.category === 'OPENED') {
    return {
      ...base,
      category: raw.category,
      ...(raw.statusV2 === 'PENDING_CLIENT' || raw.statusV2 === 'VIEWED' ? { statusV2: raw.statusV2 } : {}),
      ...(raw.sentAt ? { sentAt: raw.sentAt } : {}),
      ...(raw.nextFollowUpAt ? { nextFollowUpAt: raw.nextFollowUpAt } : {}),
    }
  }
  if (raw.category === 'ORANGE') {
    if (raw.statusV2 !== 'SIGNED' && raw.statusV2 !== 'AWAITING_PAYMENT') return null
    return {
      ...base,
      category: 'ORANGE',
      statusV2: raw.statusV2,
      ...(raw.signedAt ? { signedAt: raw.signedAt } : {}),
    }
  }
  return {
    ...base,
    category: raw.category,
    ...(raw.season ? { season: raw.season } : {}),
  }
}

export function createSupabaseOverviewMapRepository(client: AppSupabaseClient): OverviewMapRepository {
  return {
    async load(filters) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('overview_map_points_v3', {
          p_season: filters.season ?? null,
          p_include_lost: filters.includeLost,
        }),
      )
      if (response.error) {
        throw new Error(response.error.message ?? `OVERVIEW_MAP_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      }
      const parsed = resultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('OVERVIEW_MAP_RESPONSE_INVALID')

      const points = parsed.data.points
        .map(mapPoint)
        .filter((point): point is OverviewMapPoint => point !== null)

      const result: OverviewMapResult = {
        season: parsed.data.season,
        categoriesVisible: parsed.data.categoriesVisible,
        points,
        noCoordinatesCount: parsed.data.noCoordinatesCount,
        noCoordinatesRecords: parsed.data.noCoordinatesRecords.map((record) => ({
          id: record.id,
          category: record.category,
          label: record.label ?? 'Sans nom',
          ...(record.address ? { address: record.address } : {}),
          ...(record.contractId ? { contractId: record.contractId } : {}),
          ...(record.leadId ? { leadId: record.leadId } : {}),
        })),
        computedAt: parsed.data.computedAt,
        meta: { source: 'supabase', retrievedAt: new Date().toISOString() },
      }
      return result
    },
  }
}
